// Drawing App - Input Stabilizer (Smoothing / Anti-shake)

import { Point } from './types';

export type SmoothingLevel = 0 | 1 | 2 | 3; // Off / Low / Medium / High

export class Stabilizer {
  private level: SmoothingLevel = 2;
  private queue: Point[] = [];
  private readonly queueSizes: Record<SmoothingLevel, number> = {
    0: 1,
    1: 3,
    2: 6,
    3: 12
  };

  setLevel(level: SmoothingLevel): void {
    this.level = level;
    this.queue = [];
  }

  getLevel(): SmoothingLevel {
    return this.level;
  }

  reset(): void {
    this.queue = [];
  }

  process(point: Point): Point {
    if (this.level === 0) return point;

    this.queue.push(point);
    const maxSize = this.queueSizes[this.level];

    if (this.queue.length > maxSize) {
      this.queue.shift();
    }

    if (this.queue.length < 2) return point;

    // Moving average smoothing
    const smoothed = this.movingAverage();

    // Catmull-Rom interpolation for additional smoothness
    if (this.queue.length >= 4 && this.level >= 2) {
      return this.catmullRomInterpolate(smoothed);
    }

    return smoothed;
  }

  private movingAverage(): Point {
    const len = this.queue.length;
    let totalWeight = 0;
    let x = 0, y = 0, pressure = 0;

    for (let i = 0; i < len; i++) {
      // More recent points get higher weight
      const weight = (i + 1) / len;
      x += this.queue[i].x * weight;
      y += this.queue[i].y * weight;
      pressure += this.queue[i].pressure * weight;
      totalWeight += weight;
    }

    const last = this.queue[len - 1];
    return {
      x: x / totalWeight,
      y: y / totalWeight,
      pressure: pressure / totalWeight,
      tiltX: last.tiltX,
      tiltY: last.tiltY,
      timestamp: last.timestamp
    };
  }

  private catmullRomInterpolate(current: Point): Point {
    const len = this.queue.length;
    if (len < 4) return current;

    const p0 = this.queue[len - 4];
    const p1 = this.queue[len - 3];
    const p2 = this.queue[len - 2];
    const p3 = this.queue[len - 1];

    const t = 0.5; // Interpolation parameter
    const t2 = t * t;
    const t3 = t2 * t;

    // Catmull-Rom spline calculation
    const x = 0.5 * (
      (2 * p1.x) +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
    );

    const y = 0.5 * (
      (2 * p1.y) +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
    );

    // Blend between moving average and catmull-rom based on level
    const blend = this.level === 3 ? 0.7 : 0.4;
    return {
      x: current.x * (1 - blend) + x * blend,
      y: current.y * (1 - blend) + y * blend,
      pressure: current.pressure,
      tiltX: current.tiltX,
      tiltY: current.tiltY,
      timestamp: current.timestamp
    };
  }
}
